import { request } from '@/api';
import type { OssFileBizType, UploadRequestConfig } from '@/api/modules/resource/system';

/**
 * 待消费的请求条目
 */
interface PendingEntry {
  resolve: (value: string) => void;
  reject: (reason?: unknown) => void;
  config?: UploadRequestConfig;
}

/**
 * 分组数据结构：每个 ossFileBizType 对应一个分组
 */
interface BatchGroup {
  /** 所有后缀，与 entries 索引严格一一对应 */
  extList: string[];
  /** 所有原始请求条目，与 extList 索引严格一一对应 */
  entries: PendingEntry[];
}

/**
 * OSS Key 批量请求合并管理器
 *
 * 核心能力：
 * 1. 按 ossFileBizType 分组，防抖窗口内相同业务类型的请求合并为一次网络调用
 * 2. 防抖窗口结束后，对各分组并发发起请求（带并发上限）
 * 3. 请求完成后，按索引将结果精准分发回每个原始调用者
 */
class OssKeyBatchManager {
  /** 分组映射：ossFileBizType -> BatchGroup */
  private groups = new Map<OssFileBizType, BatchGroup>();

  /** 防抖定时器 */
  private timer: ReturnType<typeof setTimeout> | null = null;

  /** 防抖窗口时长（ms） */
  private debounceMs: number;

  /** 分组请求最大并发数 */
  private concurrency: number;

  constructor(debounceMs = 300, concurrency = 3) {
    this.debounceMs = debounceMs;
    this.concurrency = concurrency;
  }

  /**
   * 将单个后缀请求入队，提示责任随调用方传递，不拆分原有业务分组
   */
  enqueue(ossFileBizType: OssFileBizType, fileExt: string, config?: UploadRequestConfig): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let group = this.groups.get(ossFileBizType);
      if (!group) {
        group = { extList: [], entries: [] };
        this.groups.set(ossFileBizType, group);
      }

      // extList[i] 与 entries[i] 一一对应
      group.extList.push(fileExt);
      group.entries.push({ resolve, reject, config });

      this.scheduleFlush();
    });
  }

  /**
   * 重置防抖定时器（每次 enqueue 都会刷新窗口）
   */
  private scheduleFlush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.debounceMs);
  }

  /**
   * 防抖窗口结束 → 快照当前分组 → 清空 → 并发执行合并请求 → 分发结果
   */
  private async flush(): Promise<void> {
    const snapshot = new Map(this.groups);
    this.groups.clear();

    if (snapshot.size === 0) return;

    const tasks = [...snapshot.entries()];
    await this.runWithConcurrency(tasks, async ([bizType, group]) => {
      await this.executeGroupRequest(bizType, group);
    });
  }

  /**
   * 执行单个分组的合并请求，并按索引分发结果
   */
  private async executeGroupRequest(bizType: OssFileBizType, group: BatchGroup): Promise<void> {
    try {
      // 同组有调用方仍依赖公共提示时保留一次 Toast；全部接管时才静默
      const notificationEntry = group.entries.find((entry) => entry.config?.showErrorToast !== false) || group.entries[0];
      // extList 含重复后缀，后端按顺序生成唯一 key
      const results = await request.post<string[]>(
        '/resource/system/oss/keys/generate',
        { ossFileBizType: bizType, fileExtList: group.extList },
        { ...notificationEntry.config, allowCancel: false }
      );

      // 按索引一一对应分发（不用 Map，避免同后缀覆盖）
      for (let i = 0; i < group.entries.length; i++) {
        const entry = group.entries[i];
        const result = results.data?.[i];

        if (result !== undefined) {
          entry.resolve(result);
        } else {
          entry.reject(
            new Error(
              `[OssKeyBatch] 索引 ${i} 处未获取到生成结果` +
                `（bizType: ${bizType}, ext: ${group.extList[i]}）`
            )
          );
        }
      }
    } catch (error) {
      // 整组失败 → 所有原始调用者均 reject
      for (const entry of group.entries) {
        entry.reject(error);
      }
    }
  }

  /**
   * 通用并发限制执行器
   */
  private async runWithConcurrency<T>(items: T[], fn: (item: T) => Promise<void>): Promise<void> {
    const executing = new Set<Promise<void>>();

    for (const item of items) {
      const p: Promise<void> = fn(item).finally(() => {
        executing.delete(p);
      });
      executing.add(p);

      if (executing.size >= this.concurrency) {
        await Promise.race(executing);
      }
    }

    await Promise.all(executing);
  }
}

/**
 * 全局暴露一个管理器实例，默认防抖 300ms，最大并发 3
 */
export const ossKeyBatchManager = new OssKeyBatchManager(300, 3);
